<template>
  <div class="musubi-page musubi-page--outreach">
    <header class="musubi-page__head">
      <h1>Outreach</h1>
      <p class="text-muted">Campaigns, drip flows and the mailing lists that feed them.</p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- KPIs -->
    <div class="out-kpis">
      <MusubiKpiCard label="Campaigns" :value="counts.campaigns" icon="envelope" />
      <MusubiKpiCard label="Recipients" :value="counts.recipients" :delta="counts.sent + ' sent'" icon="users" />
      <MusubiKpiCard label="Active drips" :value="counts.activeDrips" :delta="counts.drips + ' total'" icon="envelope" />
      <MusubiKpiCard label="Drafts" :value="counts.draft" :delta="counts.draft ? 'awaiting launch' : 'clear'" :delta-dir="counts.draft ? 'down' : 'up'" icon="pen" />
    </div>

    <!-- Engagement roll-up across ALL campaigns + drips -->
    <div v-if="overview" class="out-kpis">
      <MusubiKpiCard label="Emails sent" :value="overview.totals.sent.toLocaleString()" icon="envelope" />
      <MusubiKpiCard label="Open rate" :value="overview.totals.open_rate + '%'" :delta="overview.totals.opens.toLocaleString() + ' opened'" icon="envelope-open-text" />
      <MusubiKpiCard label="Click rate" :value="overview.totals.click_rate + '%'" :delta="overview.totals.clicks.toLocaleString() + ' clicked'" icon="hand" />
      <MusubiKpiCard label="Bounced" :value="overview.totals.bounced.toLocaleString()" :delta-dir="overview.totals.bounced ? 'down' : 'up'" icon="warning" />
    </div>

    <!-- Replies. The triaged inbox already exists at /outreach/replies but was
         reachable only from the LHS nav, so the one surface that says "someone
         answered you" was the least visible thing on the page. -->
    <MusubiSectionCard v-if="replies" title="Replies" icon="arrow-left" class="out-replies">
      <template #actions>
        <a class="btn btn-sm btn-link" href="/outreach/replies">Open reply inbox &rarr;</a>
      </template>
      <div class="out-rep__counts">
        <a class="out-rep__c out-rep__c--good" href="/outreach/replies">
          <span class="out-rep__v">{{ (replies.counts.interested || 0).toLocaleString() }}</span>
          <span class="out-rep__k">interested</span>
        </a>
        <a class="out-rep__c" :class="{ 'out-rep__c--todo': replies.counts.needs_review }" href="/outreach/replies">
          <span class="out-rep__v">{{ (replies.counts.needs_review || 0).toLocaleString() }}</span>
          <span class="out-rep__k">need a human</span>
        </a>
        <a class="out-rep__c" href="/outreach/replies">
          <span class="out-rep__v">{{ (replies.counts.all || 0).toLocaleString() }}</span>
          <span class="out-rep__k">replies in total</span>
        </a>
      </div>
      <table v-if="replies.rows.length" class="out-table out-rep__tbl">
        <thead><tr><th>Who replied</th><th>Read as</th><th>From</th><th>When</th></tr></thead>
        <tbody>
          <tr v-for="r in replies.rows" :key="r.id">
            <td class="out-nm">
              <a v-if="r.prospect_id" :href="prospectHref(r.prospect_id)">{{ r.subscriber_name || r.subscriber_email || '&mdash;' }}</a>
              <template v-else>{{ r.subscriber_name || r.subscriber_email || '&mdash;' }}</template>
              <span v-if="r.company_name" class="out-rep__co">{{ r.company_name }}</span>
            </td>
            <td><MusubiStatusPill :status="r.classification || 'other'" :label="replyLabel(r.classification)" /></td>
            <td class="out-rep__src">{{ r.source_campaign || r.source_flow || '&mdash;' }}</td>
            <td class="out-rep__when">{{ ago(r.created) }}</td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-muted small py-3">Nobody has replied yet. Replies land here the moment they arrive.</p>
    </MusubiSectionCard>

    <!-- Lifecycle ribbon -->
    <MusubiJourneyRibbon v-model="activeStage" :stages="ribbon" class="out-ribbon" @select="onStage" />

    <div class="out-grid">
      <!-- Recent campaigns -->
      <MusubiSectionCard :title="recentTitle" icon="envelope">
        <template #actions>
          <a class="btn btn-sm btn-link" href="/outreach/campaigns">All campaigns →</a>
        </template>
        <div v-if="loading" class="text-muted small py-3">Loading…</div>
        <table v-else-if="recent.length" class="out-table">
          <thead><tr><th>Campaign</th><th>Status</th><th>Recipients</th></tr></thead>
          <tbody>
            <tr v-for="c in recent" :key="c.id">
              <td class="out-nm">{{ c.name || '—' }}</td>
              <td><MusubiStatusPill :status="c.status || 'draft'" /></td>
              <td>{{ (c.total_recipients || 0).toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small py-3">No campaigns in this stage.</p>
      </MusubiSectionCard>

      <!-- Quick links / sub-areas -->
      <MusubiSectionCard title="Build &amp; audiences" icon="wrench">
        <a class="out-link" href="/outreach/campaigns"><CygnusIcon name="envelope" /><div><b>Campaigns</b><span>One-shot sends &amp; launches</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/drip-flows"><CygnusIcon name="envelope" /><div><b>Drip flows</b><span>Automated multi-step nurture</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/lists"><CygnusIcon name="list" /><div><b>Mailing lists</b><span>Subscriber audiences</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/segments"><CygnusIcon name="filter" /><div><b>Segments</b><span>Dynamic audience slices</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/replies"><CygnusIcon name="arrow-left" /><div><b>Replies</b><span>AI-triaged reply inbox</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/messages"><CygnusIcon name="refresh" /><div><b>Send history</b><span>Per-message delivery log</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
        <a class="out-link" href="/outreach/deliverability"><CygnusIcon name="shield" /><div><b>Deliverability</b><span>Sending health, SPF/DKIM/DMARC &amp; warm-up</span></div><CygnusIcon name="chevron-right" class="out-chev" /></a>
      </MusubiSectionCard>
    </div>

    <!-- Top performers (by open rate) -->
    <div v-if="overview && (overview.top_campaigns.length || overview.top_flows.length)" class="out-grid mt-1">
      <MusubiSectionCard title="Top campaigns" subtitle="by open rate" icon="award">
        <table v-if="overview.top_campaigns.length" class="out-table">
          <thead><tr><th>Campaign</th><th class="text-end">Sent</th><th class="text-end">Open</th><th class="text-end">Click</th></tr></thead>
          <tbody>
            <tr v-for="c in overview.top_campaigns" :key="c.id">
              <td class="out-nm">{{ c.name }}</td>
              <td class="text-end">{{ c.sent.toLocaleString() }}</td>
              <td class="text-end">{{ c.open_rate }}%</td>
              <td class="text-end">{{ c.click_rate }}%</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small py-3">No campaign sends yet.</p>
      </MusubiSectionCard>
      <MusubiSectionCard title="Top drip flows" subtitle="by open rate" icon="award">
        <table v-if="overview.top_flows.length" class="out-table">
          <thead><tr><th>Flow</th><th class="text-end">Sent</th><th class="text-end">Open</th><th class="text-end">Click</th></tr></thead>
          <tbody>
            <tr v-for="f in overview.top_flows" :key="f.id">
              <td class="out-nm">{{ f.name }}</td>
              <td class="text-end">{{ f.sent.toLocaleString() }}</td>
              <td class="text-end">{{ f.open_rate }}%</td>
              <td class="text-end">{{ f.click_rate }}%</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small py-3">No drip sends yet.</p>
      </MusubiSectionCard>
    </div>
  </div>
</template>

<script>
/**
 * AdminOutreach — Part 2 (Outreach) section landing. Composes the design-
 * system primitives over live /api/v1/admin/campaigns + /drip-flows. Status
 * counts computed client-side; the lifecycle ribbon filters the recent list.
 */
import useApi from "@apexl/icarus-core/src/services/api";

/** Mirrors ReplyClassification::ALL - kept short enough to sit in a pill. */
const REPLY_LABELS = {
  interested: 'interested',
  not_now: 'not now',
  wrong_person: 'wrong person',
  out_of_office: 'out of office',
  unsubscribe_request: 'unsubscribe',
  other: 'other',
};

export default {
  name: 'AdminOutreach',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(), campaigns: [], drips: [], overview: null, loading: false, error: null, activeStage: null,
      // null until the reply inbox answers. It is plan-gated (403 on Starter),
      // so staying null is a legitimate outcome and the card simply never
      // renders - the same silent degrade the drip/overview calls already use.
      replies: null,
    };
  },
  mounted() { this.load(); },
  computed: {
    counts() {
      const c = this.campaigns;
      return {
        campaigns: c.length,
        recipients: c.reduce((n, x) => n + (Number(x.total_recipients) || 0), 0),
        sent: c.filter((x) => x.status === 'sent' || x.status === 'completed').length,
        draft: c.filter((x) => x.status === 'draft').length,
        scheduled: c.filter((x) => x.status === 'scheduled').length,
        sending: c.filter((x) => x.status === 'sending').length,
        drips: this.drips.length,
        activeDrips: this.drips.filter((d) => d.status === 'active').length,
      };
    },
    ribbon() {
      const c = this.counts;
      return [
        { key: 'draft',     label: 'Draft',     count: c.draft,     icon: 'pen' },
        { key: 'scheduled', label: 'Scheduled', count: c.scheduled, icon: 'calendar' },
        { key: 'sending',   label: 'Sending',   count: c.sending,   icon: 'envelope' },
        { key: 'sent',      label: 'Sent',      count: c.sent,      icon: 'check-circle' },
      ];
    },
    recent() {
      const f = this.activeStage
        ? (c) => (this.activeStage === 'sent' ? (c.status === 'sent' || c.status === 'completed') : c.status === this.activeStage)
        : () => true;
      return [...this.campaigns].filter(f).sort((a, b) => (b.created || 0) - (a.created || 0)).slice(0, 8);
    },
    recentTitle() { return this.activeStage ? `Campaigns · ${this.activeStage}` : 'Recent campaigns'; },
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const [cRes, dRes, oRes, rRes] = await Promise.all([
          this.api.get('/api/v1/admin/campaigns'),
          this.api.get('/api/v1/admin/drip-flows').catch(() => null),
          this.api.get('/api/v1/admin/outreach/overview').catch(() => null),
          // per_page=5 gets the latest handful AND the full counts map: the
          // count queries in ReplyTriageReadService run independently of the
          // row page, so this is the cheap summary call, not a second one.
          this.api.get('/api/v1/admin/reply-triage?per_page=5').catch(() => null),
        ]);
        this.campaigns = this.unwrap(cRes);
        this.drips = dRes ? this.unwrap(dRes) : [];
        this.overview = oRes ? (oRes?.data ?? oRes) : null;
        this.replies = this.unwrapReplies(rRes);
      } catch (e) {
        this.error = e?.message || 'Failed to load outreach data.';
      }
      this.loading = false;
    },
    unwrap(res) {
      const p = res?.data?.data ?? res?.data ?? res;
      return Array.isArray(p) ? p : (p?.rows || p?.data || []);
    },
    onStage(s) { if (this.activeStage === s.key) this.activeStage = null; },
    /** {rows, counts} or null when the inbox is gated / unreachable. */
    unwrapReplies(res) {
      if (!res) return null;
      const p = res?.data ?? res;
      if (!p || typeof p !== 'object' || !p.counts) return null;
      return { rows: Array.isArray(p.rows) ? p.rows : [], counts: p.counts };
    },
    prospectHref(id) { return '/acquire/prospects/' + id + '?from=outreach'; },
    replyLabel(c) { return REPLY_LABELS[c] || 'other'; },
    /**
     * Coarse age. The reply BODY is deliberately never stored (see the
     * reply_triage migration), so who / how it read / when is the honest limit
     * of what this card can show without opening the inbox.
     */
    ago(epoch) {
      if (!epoch) return '-';
      const secs = Math.max(0, Math.floor(Date.now() / 1000) - (Number(epoch) || 0));
      if (secs < 3600) return Math.max(1, Math.floor(secs / 60)) + 'm ago';
      if (secs < 86400) return Math.floor(secs / 3600) + 'h ago';
      return Math.floor(secs / 86400) + 'd ago';
    },
  },
};
</script>

<style scoped>
.out-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 18px 0; }
.out-ribbon { margin-bottom: 18px; }
.out-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 16px; }
.out-table { width: 100%; border-collapse: collapse; }
.out-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 8px 6px; border-bottom: 1px solid var(--m-border); }
.out-table td { padding: 10px 6px; border-bottom: 1px solid var(--m-border); vertical-align: middle; }
.out-nm { font-weight: 650; }
.out-link { display: flex; align-items: center; gap: 12px; padding: 11px 8px; border-bottom: 1px solid var(--m-border); text-decoration: none; color: var(--m-text-primary); }
.out-link:last-child { border-bottom: none; }
.out-link:hover { color: var(--m-accent); }
.out-link > i:first-child { color: var(--m-accent); width: 20px; text-align: center; }
.out-link b { display: block; font-size: 0.88rem; }
.out-link span { color: var(--m-text-tertiary); font-size: 0.75rem; }
.out-chev { margin-left: auto; color: var(--m-text-tertiary); }
.out-replies { margin-bottom: 18px; }
.out-rep__counts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.out-rep__c { display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border: 1px solid var(--m-border); border-radius: 8px; text-decoration: none; color: var(--m-text-primary); }
.out-rep__c:hover { border-color: var(--m-accent); }
.out-rep__c--good { background: color-mix(in srgb, var(--m-accent-2) 10%, transparent); border-color: color-mix(in srgb, var(--m-accent-2) 30%, transparent); }
.out-rep__c--todo { background: #FFFBEB; border-color: #FDE68A; }
.out-rep__v { font-size: 1.32rem; font-weight: 750; line-height: 1.1; }
.out-rep__k { font-size: 0.7rem; color: var(--m-text-tertiary); }
.out-rep__tbl { margin-top: 10px; }
.out-rep__co { display: block; font-size: 0.72rem; font-weight: 400; color: var(--m-text-tertiary); }
.out-rep__src, .out-rep__when { font-size: 0.8rem; color: var(--m-text-secondary); }
@media (max-width: 640px) { .out-rep__counts { grid-template-columns: 1fr; } }
@media (max-width: 1024px) { .out-kpis { grid-template-columns: repeat(2, 1fr); } .out-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px)  { .out-kpis { grid-template-columns: 1fr; } }
</style>
